import { Hash } from 'lucide-react';
import type { BuilderInput, FieldSpec, EditorSection, OutcomeSpec } from '@/lib/editor/types';
import { cn } from '@/lib/cn';
import { SECTION_ICONS } from './constants';
import { CatalogField } from './CatalogField';
import { FormSection } from './FormSection';
import { GenericOutcomePicker } from './GenericOutcomePicker';
import { ReasonField } from './ReasonField';
import type { ActivateFn, Translate } from './types';

function outcomeLabel(short: string, value: string, t: Translate): string {
  const area = short.split('.')[0];
  const key = `try.${area}.outcome${value}`;
  const translated = t(key);
  return translated !== key ? translated : value;
}

export function StructuredSectionFields({
  section,
  input,
  outcome,
  rejectReason,
  patch,
  setOutcome,
  setRejectReason,
  highlighted,
  onActivate,
  fieldByKey,
  outcomeSpec,
  builderShort,
  t,
  compact = false,
}: {
  section: EditorSection;
  input: BuilderInput;
  outcome: string;
  rejectReason: string;
  patch: (key: string, value: string | boolean) => void;
  setOutcome: (v: string) => void;
  setRejectReason: (v: string) => void;
  highlighted: string | null;
  onActivate: ActivateFn;
  fieldByKey: (key: string) => FieldSpec | undefined;
  outcomeSpec: OutcomeSpec;
  builderShort: string;
  t: Translate;
  compact?: boolean;
}) {
  const cols = section.columns ?? 1;
  const icon = SECTION_ICONS[section.id] ?? Hash;
  const rejectWhen = outcomeSpec.rejectWhen;
  const showReject = Array.isArray(rejectWhen) ? rejectWhen.includes(outcome) : outcome === rejectWhen;
  const reasonCodes = fieldByKey('rejectReason')?.options ?? outcomeSpec.rejectOptions ?? [];

  return (
    <FormSection icon={icon} title={t(section.titleKey)}>
      <div className={cn(cols === 2 ? 'grid grid-cols-2 gap-2' : compact ? 'space-y-2' : 'space-y-2.5')}>
        {section.keys.map((key) => {
          if (key === 'outcome') {
            return (
              <GenericOutcomePicker
                key={key}
                options={outcomeSpec.options}
                outcome={outcome}
                setOutcome={setOutcome}
                highlighted={highlighted === 'outcome'}
                onActivate={onActivate}
                labelFor={(v) => outcomeLabel(builderShort, v, t)}
                t={t}
                compact
              />
            );
          }
          if (key === 'rejectReason') {
            if (!showReject || reasonCodes.length === 0) return null;
            return (
              <ReasonField
                key={key}
                value={rejectReason}
                setRejectReason={setRejectReason}
                highlighted={highlighted === 'rejectReason'}
                onActivate={onActivate}
                t={t}
                compact
                codes={reasonCodes}
              />
            );
          }
          const spec = fieldByKey(key);
          if (!spec || spec.kind === 'checkbox') return null;
          return (
            <CatalogField
              key={key}
              spec={spec}
              value={String(input[key] ?? '')}
              onChange={(v) => patch(key, v)}
              highlighted={highlighted === key}
              onActivate={onActivate}
              t={t}
              compact={compact}
            />
          );
        })}
      </div>
    </FormSection>
  );
}
